import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth.store";
import { StorageService } from "@/services/storage.service";
import type { AuthResponse } from "@/types/auth";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3002";

// Em produção, VITE_API_URL deve apontar para a API real (não localhost)
if (import.meta.env.PROD && API_URL.includes("localhost")) {
  console.warn("[API] VITE_API_URL aponta para localhost em produção — as chamadas à API vão falhar. Configure .env.production ou variável no deploy.");
}

/** Máximo de retentativas para erros de servidor (cold start) */
const MAX_SERVER_RETRIES = 3;
/** Máximo de retentativas para refresh quando o servidor está indisponível */
const MAX_REFRESH_RETRIES = 3;
/** Atrasos em ms para backoff (cold start) */
const RETRY_DELAYS = [2000, 4000, 8000];

/** Renovar access token quando faltar menos que isso até o exp (evita rajadas de 401) */
const PROACTIVE_REFRESH_MS = 120_000;
/** Margem para relógio / skew ao interpretar exp do JWT */
const JWT_SKEW_MS = 60_000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Indica se o erro é de servidor/rede (cold start, 502/503/504, timeout) — não é 401 de token inválido */
function isServerOrNetworkError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  const code = (err as { code?: string })?.code;
  if (status && status >= 500 && status < 600) return true;
  if (code === "ECONNREFUSED" || code === "ETIMEDOUT" || code === "ERR_NETWORK") return true;
  return false;
}

/** Decodifica payload JWT (sem validar assinatura — só leitura de exp). */
function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as { exp?: number };
  } catch {
    return null;
  }
}

/** exp em ms desde epoch, ou null se não der para ler */
function getJwtExpMs(token: string | null): number | null {
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (payload?.exp == null || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
}

/** Access token ainda dentro da validade (com margem de skew). */
function isAccessTokenProbablyValid(token: string | null): boolean {
  const expMs = getJwtExpMs(token);
  if (expMs === null) return false;
  return expMs > Date.now() - JWT_SKEW_MS;
}

/** Access token expirado ou sem exp legível — vale tentar refresh. */
function shouldTryRefreshForExpiredAccess(token: string | null): boolean {
  const expMs = getJwtExpMs(token);
  if (expMs === null) return true;
  return expMs <= Date.now() + JWT_SKEW_MS;
}

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

function clearAuthAndRedirect(): void {
  StorageService.clear();
  localStorage.removeItem("auth-storage");
  try {
    const { signOut } = useAuthStore.getState();
    signOut().catch(() => {});
  } catch {
    // ignore
  }
  window.location.replace("/login");
}

export type RefreshResult = "ok" | "session_invalid" | "unavailable";

type RefreshExecutionResult =
  | { status: "ok"; auth: AuthResponse }
  | { status: "session_invalid" | "unavailable" };

let refreshPromise: Promise<RefreshExecutionResult> | null = null;

/**
 * BroadcastChannel para sincronizar tokens entre abas do mesmo origin.
 * Quando uma aba faz o refresh, transmite os novos tokens para as outras —
 * evitando que abas em background tentem usar o token antigo (já marcado como usado)
 * e disparem o mecanismo de revogação por reuso.
 */
const authChannel = (() => {
  try {
    return typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("auth_tokens") : null;
  } catch {
    return null;
  }
})();

type AuthChannelMessage =
  | { type: "tokens_refreshed"; accessToken: string; refreshToken: string }
  | { type: "session_invalidated" };

/** Propaga novos tokens para outras abas após refresh bem-sucedido. */
function broadcastTokensRefreshed(accessToken: string, refreshToken: string): void {
  try {
    authChannel?.postMessage({ type: "tokens_refreshed", accessToken, refreshToken } satisfies AuthChannelMessage);
  } catch {
    // ignore — BroadcastChannel pode falhar em contextos restritos
  }
}

/** Registra listener para receber tokens atualizados de outras abas. */
if (authChannel) {
  authChannel.onmessage = (event: MessageEvent<AuthChannelMessage>) => {
    const msg = event.data;
    if (msg.type === "tokens_refreshed") {
      // Outra aba já fez o refresh — atualiza localStorage localmente sem fazer nova requisição
      StorageService.setAccessToken(msg.accessToken);
      StorageService.setRefreshToken(msg.refreshToken);
    } else if (msg.type === "session_invalidated") {
      clearAuthAndRedirect();
    }
  };
}

async function executeTokenRefresh(): Promise<RefreshExecutionResult> {
  let refreshToken = StorageService.getRefreshToken();
  if (!refreshToken) return { status: "session_invalid" };

  for (let attempt = 0; attempt <= MAX_REFRESH_RETRIES; attempt++) {
    try {
      const res = await axios.post<AuthResponse>(`${API_URL}/auth/refresh`, { refreshToken });
      StorageService.setAccessToken(res.data.accessToken);
      StorageService.setRefreshToken(res.data.refreshToken);
      StorageService.setUser(res.data.user);
      // Propaga os novos tokens para outras abas antes de retornar
      broadcastTokensRefreshed(res.data.accessToken, res.data.refreshToken);
      try {
        useAuthStore.getState().updateUser(res.data.user);
      } catch {
        // ignore
      }
      return { status: "ok", auth: res.data };
    } catch (refreshErr) {
      const refreshStatus = (refreshErr as { response?: { status?: number } })?.response?.status;
      if (refreshStatus === 401) {
        // Outra aba pode ter rotacionado o token — re-lê storage (BroadcastChannel) e tenta de novo
        const latestRefresh = StorageService.getRefreshToken();
        if (latestRefresh && latestRefresh !== refreshToken) {
          refreshToken = latestRefresh;
          continue;
        }
        if (attempt === 0) {
          await sleep(400);
          const afterWait = StorageService.getRefreshToken();
          if (afterWait && afterWait !== refreshToken) {
            refreshToken = afterWait;
            continue;
          }
        }
        return { status: "session_invalid" };
      }
      if (isServerOrNetworkError(refreshErr) && attempt < MAX_REFRESH_RETRIES) {
        await sleep(RETRY_DELAYS[attempt] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1]);
        continue;
      }
      if (isServerOrNetworkError(refreshErr)) return { status: "unavailable" };
      return { status: "session_invalid" };
    }
  }
  return { status: "unavailable" };
}

export function getSharedRefresh(): Promise<RefreshResult> {
  if (!refreshPromise) {
    refreshPromise = executeTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise.then((result) => result.status);
}

export async function getSharedRefreshAuth(): Promise<AuthResponse> {
  if (!refreshPromise) {
    refreshPromise = executeTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  const result = await refreshPromise;
  if (result.status !== "ok") {
    throw new Error(result.status === "unavailable" ? "Refresh unavailable" : "Session invalid");
  }
  return result.auth;
}

// Interceptor: refresh proativo antes do token expirar + Authorization
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const url = config.url ?? "";
  if (url.includes("/auth/refresh")) {
    return config;
  }

  const access = StorageService.getAccessToken();
  const refresh = StorageService.getRefreshToken();

    if (access && refresh) {
      const expMs = getJwtExpMs(access);
      if (expMs !== null && expMs - Date.now() < PROACTIVE_REFRESH_MS) {
        // Refresh proativo é otimização — falha não deve deslogar se o access ainda for válido.
        await getSharedRefresh();
      }
    }

  const token = StorageService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Axios serializa `null` como a string "null", que o Nest rejeita como JSON inválido.
  if (config.data === null) {
    config.data = {};
  }

  return config;
});

type ExtendedConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _retryCount?: number;
  skipAuthRedirect?: boolean;
};

// Interceptor: cold start, 401 + refresh sob demanda
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: {
    response?: { status?: number };
    config: ExtendedConfig;
    code?: string;
    message?: string;
  }) => {
    const status = error.response?.status;
    const config = error.config as ExtendedConfig;
    const retryCount = config._retryCount ?? 0;

    // 502/503/504 ou erro de rede: retentar a mesma requisição (tolerância a cold start)
    if (
      (status === 502 || status === 503 || status === 504) ||
      (error.code === "ECONNREFUSED" || error.code === "ETIMEDOUT" || error.code === "ERR_NETWORK")
    ) {
      if (retryCount < MAX_SERVER_RETRIES) {
        const delay = RETRY_DELAYS[retryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
        await sleep(delay);
        config._retryCount = retryCount + 1;
        return api(config);
      }
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    if (config.skipAuthRedirect) {
      return Promise.reject(error);
    }

    const isRefreshRequest = config?.url?.includes("/auth/refresh");
    if (isRefreshRequest) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    const refreshToken = StorageService.getRefreshToken();
    if (!refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    const accessToken = StorageService.getAccessToken();

    // 401 com JWT ainda dentro do exp: provável falta de permissão / recurso — não desloga nem gasta refresh
    if (isAccessTokenProbablyValid(accessToken)) {
      return Promise.reject(error);
    }

    // Já tentou uma vez após refresh — não força logout (pode ser 401 de negócio)
    if (config._retry) {
      return Promise.reject(error);
    }

    if (!shouldTryRefreshForExpiredAccess(accessToken)) {
      return Promise.reject(error);
    }

    try {
      const result = await getSharedRefresh();
      if (result === "session_invalid") {
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
      if (result === "unavailable") {
        return Promise.reject(error);
      }

      config._retry = true;
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${StorageService.getAccessToken()}`;
      return api(config);
    } catch {
      return Promise.reject(error);
    }
  },
);

export default api;
